import styled, { css, keyframes } from 'styled-components';

import {
  COLORS,
  MOBILE,
  REDUCED_MOTION,
  BRAND_DEPLOYMENT_ANIM,
  BRAND_DEPLOYMENT_ARROW,
  BRAND_DEPLOYMENT_COMPLETED_GLOW,
  BRAND_DEPLOYMENT_SURFACE,
  BRAND_DEPLOYMENT_TITLE_SHINE_MS,
  GRADIENTS,
  BRAND_DEPLOYMENT_GLASS,
  BRAND_DEPLOYMENT_GLASS_EDGE,
} from '../../../../theme/tokens';
import { Text } from '../../../../components';

/**
 * "Brand Deployment" section shell. A neutral bordered surface that sits
 * between the "Spotlight" banner and the "Business Optimization Score" gauges.
 *
 * The WebGL aurora ({@link AuroraField}) paints at the back; a frosted-glass layer
 * over it diffuses the motion; content sits on top. `overflow: hidden` clips it all
 * to the rounded rect; `isolation` keeps the stacking self-contained.
 *
 * The panel hairline is an OVERLAY ring, not a real `border`. A real one pushed the aurora
 * inward by its own width: the canvas is absolutely positioned with `inset: 0`, which
 * resolves against the PADDING box, so a 1px border left a 1px line of bare surface above
 * the colour. Widening the canvas instead would not help, because `overflow: hidden` clips
 * to that same padding box. Drawing the hairline on top lets the aurora reach the true top
 * edge while the panel keeps the outline it shares with every other panel.
 */
export const Root = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: ${BRAND_DEPLOYMENT_SURFACE};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    border: 1px solid ${COLORS.panelBorder};
    pointer-events: none;
  }

  ${MOBILE} {
    gap: 16px;
    padding: 16px;
  }
`;

/**
 * Frosted glass IN FRONT of the WebGL aurora: a `backdrop-filter` blur diffuses the
 * moving colour behind it into soft light, while a translucent tint keeps the card
 * reading as a surface. A faint top highlight sells the glass edge. Above the aurora
 * (z-index 1), below the content (z-index 2). When backdrop-filter is unsupported,
 * the tint alone still gives a usable scrim.
 */
export const Glass = styled.span`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${BRAND_DEPLOYMENT_GLASS};
  backdrop-filter: blur(40px) saturate(1.2);
  -webkit-backdrop-filter: blur(40px) saturate(1.2);
  border-top: 1px solid ${BRAND_DEPLOYMENT_GLASS_EDGE};
`;

/**
 * The static "success aurora" shown IN PLACE of the live WebGL aurora once the deploy
 * completes — a soft green/teal glow blooming from the top over the near-black base.
 * Sits at the back (z-index 0); no glass over it (the gradient is already soft).
 */
export const CompletedGlow = styled.span`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: ${BRAND_DEPLOYMENT_COMPLETED_GLOW};
`;

/** Header row: the Lumio logo lockup, the section title, and a right-aligned controls
 *  cluster (the strip's browse chevrons, plus "Close" once done). */
export const Header = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Wraps the "Deploying {brand}" title and its live ellipsis on one baseline. */
export const TitleGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 7px;
  min-width: 0;
`;

/** Section title — light eyebrow, matching the Spotlight header scale. The plain
 *  variant (used for "Completed"); the in-progress title adds the shine below. */
export const Title = styled(Text)`
  font-size: 20px;
  font-weight: 300;
  line-height: 1.2;
  color: ${COLORS.heading};
`;

/** reactbits "Shiny Text": a muted grey base with a bright glint that a
 *  background-position loop sweeps across — shown ONLY while the deploy is in
 *  progress ("Deploying {brand}"). */
const titleShine = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
`;

export const ShinyTitle = styled(Title)`
  color: transparent;
  background: ${GRADIENTS.deploymentTitleShine};
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${titleShine} ${BRAND_DEPLOYMENT_TITLE_SHINE_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
    color: ${COLORS.heading};
    -webkit-text-fill-color: ${COLORS.heading};
  }
`;

/** Flexible middle of the header: an empty spacer when expanded (pushing the controls to the
 *  right edge), and the home of the collapsed-state progress bar. Being the only flex-growing
 *  child, it deterministically right-aligns {@link HeaderControls} either way. */
export const HeaderMiddle = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  /* Breathing room so the collapsed progress bar doesn't run right up to the title / toggle. */
  padding: 0 8px;
`;

/** Right-aligned cluster in the section header: the strip's browse chevrons, the "Close"
 *  affordance once the deploy is done, and the collapse / expand toggle. */
export const HeaderControls = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Right-aligned "Close" text affordance, shown once the deploy is complete; it
 *  dismisses the whole section. */
export const CloseButton = styled.button`
  flex-shrink: 0;
  padding: 4px 8px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition: color ${BRAND_DEPLOYMENT_ANIM.LINK_MS}ms ease;

  &:hover {
    color: ${COLORS.text};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.text};
    outline-offset: 2px;
    border-radius: 6px;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/**
 * COLLAPSED-state progress track: a slim frosted pill that fills the header's middle, between
 * the title and the expand control. Sits on the header row, so it never adds height. Same
 * frosted palette as the chevron pill for a cohesive control cluster.
 */
export const ProgressTrack = styled.div`
  flex: 1;
  min-width: 0;
  height: 8px;
  border-radius: 999px;
  border: 1px solid ${BRAND_DEPLOYMENT_ARROW.border};
  background: ${BRAND_DEPLOYMENT_ARROW.bg};
  overflow: hidden;
`;

const progressSheen = keyframes`
  0% {
    transform: translateX(-100%);
  }
  60%,
  100% {
    transform: translateX(220%);
  }
`;

/**
 * The gradient fill — the aurora sweep, its width eased to the deploy progress. While the
 * deploy is still running (`$active`) a soft white sheen travels across it; once complete it
 * rests full and static.
 */
export const ProgressFill = styled.div<{ $pct: number; $active: boolean }>`
  position: relative;
  height: 100%;
  width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
  border-radius: inherit;
  overflow: hidden;
  background: ${GRADIENTS.deploymentProgress};
  transition: width ${BRAND_DEPLOYMENT_ANIM.PROGRESS_MS}ms cubic-bezier(0.22, 1, 0.36, 1);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GRADIENTS.deploymentProgressSheen};
    transform: translateX(-100%);
    ${(p) =>
      p.$active &&
      css`
        animation: ${progressSheen} ${BRAND_DEPLOYMENT_ANIM.PROGRESS_SHEEN_MS}ms ease-in-out infinite;
      `}
  }

  ${REDUCED_MOTION} {
    transition: none;

    &::after {
      animation: none;
    }
  }
`;

/**
 * The collapse / expand toggle — a frosted round button matching the chevron pill, sitting at
 * the header's right edge. A single chevron points UP when expanded (click to collapse) and
 * flips DOWN when collapsed (click to expand).
 */
export const CollapseToggle = styled.button`
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid ${BRAND_DEPLOYMENT_ARROW.border};
  background: ${BRAND_DEPLOYMENT_ARROW.bg};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: ${BRAND_DEPLOYMENT_ARROW.glyph};
  cursor: pointer;
  transition: transform ${BRAND_DEPLOYMENT_ANIM.LINK_MS}ms ease;

  & > svg {
    width: 16px;
    height: 16px;
    transition: transform ${BRAND_DEPLOYMENT_ANIM.HOVER_MS}ms ease;
  }

  /* Collapsed: shrink to the icon/title line height so the collapsed header stays exactly
     "lumio icon + title + existing padding" tall (the strip below is gone), and flip the
     chevron to point DOWN (expand). */
  &[data-collapsed='true'] {
    width: 28px;
    height: 24px;
  }

  &[data-collapsed='true'] > svg {
    transform: rotate(180deg);
  }

  &:hover {
    transform: scale(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND_DEPLOYMENT_ARROW.glyph};
    outline-offset: 2px;
  }

  ${REDUCED_MOTION} {
    transition: none;

    & > svg {
      transition: none;
    }
  }
`;
